import { LINE_SERIES_CAP_RATIO } from "../constants";
import { createLineRenderer } from "../gl/lineRenderer";
import { createRectRenderer, RECT_PALETTE_SLOTS } from "../gl/rectRenderer";
import { createFillRenderer } from "../gl/fillRenderer";
import { createMarkerRenderer } from "../gl/markerRenderer";
import { lineWidthPx, bodyWidthPx, centredOffset, buildRectShapes } from "../series/rectShapes";
import { buildLineSegments, buildHistogramColumns, lastFiniteValue, SLOTS_PER_HISTOGRAM } from "./indicatorGeometry";
import { buildFillSegments, buildMarkers } from "./shapeGeometry";
import { createPlotLayer } from "./plotLayer";
import { createLevelRenderer } from "./levelRenderer";

const RGBA_COMPONENTS = 4;

/**
 * GPU geometry of one indicator, bottom up: levels of an oscillator and the
 * background between them (levelRenderer), backgrounds between lines,
 * histogram columns, lines and markers (dots, triangles), added to the
 * WebGL layer while the indicator is on the chart. `rebuild` writes all
 * instances, `updateFrom` rewrites the tail in place (a tick), pan and zoom
 * change only transforms.
 *
 * @param {object} glLayer
 */
export const createIndicatorRenderer = (glLayer) => {
  const fills = createFillRenderer();
  const columns = createRectRenderer();
  const lines = createLineRenderer();
  const markers = createMarkerRenderer();
  const levels = createLevelRenderer(glLayer);
  const palette = new Float32Array(RECT_PALETTE_SLOTS * RGBA_COMPONENTS);
  let priceBase = 0;

  const layers = {
    fills: createPlotLayer(fills, true, ({ plot, data, first, count, priceBase: base }) =>
      buildFillSegments({
        plot,
        series: data.series,
        first,
        count,
        priceBase: base,
        floatsPerInstance: fills.floatsPerInstance,
      }),
    ),
    histograms: createPlotLayer(columns, false, ({ plot, plotIndex, data, first, count, priceBase: base }) =>
      buildHistogramColumns({
        ...plot,
        values: data.series[plot.key],
        bars: data.bars,
        upSlot: plotIndex * SLOTS_PER_HISTOGRAM,
        firstIndex: first,
        count,
        priceBase: base,
        floatsPerRect: columns.floatsPerInstance,
      }),
    ),
    lines: createPlotLayer(lines, true, ({ plot, data, first, count, priceBase: base }) =>
      buildLineSegments({
        ...plot,
        values: data.series[plot.key],
        firstSegment: first,
        segmentCount: count,
        priceBase: base,
        floatsPerLine: lines.floatsPerInstance,
      }),
    ),
    markers: createPlotLayer(markers, false, ({ plot, data, first, count, priceBase: base }) =>
      buildMarkers({
        plot,
        series: data.series,
        first,
        count,
        priceBase: base,
        floatsPerInstance: markers.floatsPerInstance,
      }),
    ),
  };

  [fills, columns, lines, markers].forEach((renderer) => glLayer.addRenderer(renderer));

  return {
    /**
     * @param {object} params
     * @param {object} params.plots - visible plots (shownPlots.buildShownPlots)
     * @param {Object<string, number[]>} params.series
     * @param {object[]} params.bars
     */
    rebuild: ({ plots, series, bars }) => {
      const data = { series, bars };

      priceBase = lastFiniteValue(plots.valuePlots, series);
      palette.fill(0);
      plots.histograms.forEach((plot, plotIndex) => {
        palette.set(plot.color, plotIndex * SLOTS_PER_HISTOGRAM * RGBA_COMPONENTS);
        palette.set(plot.downColor || plot.color, (plotIndex * SLOTS_PER_HISTOGRAM + 1) * RGBA_COMPONENTS);
      });
      Object.entries(layers).forEach(([kind, layer]) => layer.rebuild(plots[kind], data, priceBase));
      levels.setLevels(plots.levels);
    },

    /**
     * Rewrites the instances from `fromIndex` on; the bar count is that of the last rebuild.
     *
     * @param {{ series: object, bars: object[] }} data
     * @param {number} fromIndex - first value that changed
     */
    updateFrom: (data, fromIndex) => {
      Object.values(layers).forEach((layer) => layer.updateFrom(data, fromIndex));
    },

    /**
     * @param {object} params
     * @param {number[]} params.viewport - [width, height] of the canvas, device px
     * @param {object} params.timeScale
     * @param {object} params.priceScale - of the pane
     * @param {number} params.top - top of the pane in the chart, CSS px
     * @param {number} params.pixelRatio
     * @param {boolean} params.isSnappedX - see rectRenderer.draw
     */
    draw: ({ viewport, timeScale, priceScale, top, pixelRatio, isSnappedX }) => {
      const { scale, offset } = priceScale.relativeTransform(priceBase);
      const xTransform = [timeScale.barSpacing * pixelRatio, timeScale.offset * pixelRatio];
      const yTransform = [scale * pixelRatio, (offset + top) * pixelRatio];
      const columnWidth = bodyWidthPx(timeScale.barSpacing, pixelRatio, lineWidthPx(pixelRatio));
      const shapes = buildRectShapes([[columnWidth, centredOffset(columnWidth)]], lineWidthPx(pixelRatio));

      levels.draw({ viewport, priceScale, top, pixelRatio });
      fills.draw({ viewport, xTransform, yTransform });
      columns.draw({ viewport, xTransform, yTransform, shapes, palette, isSnappedX });
      lines.draw({
        viewport,
        xTransform,
        yTransform,
        widthScale: pixelRatio,
        capRatio: LINE_SERIES_CAP_RATIO,
      });
      markers.draw({ viewport, xTransform, yTransform, pixelRatio });
    },

    release: () => {
      levels.release();
      [fills, columns, lines, markers].forEach((renderer) => glLayer.removeRenderer(renderer));
    },
  };
};
